import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import AssignmentPicker, { type AssignmentCandidate } from "@/components/shared/AssignmentPicker";
import UserAvatar from "@/components/UserAvatar";
import { getDisplayName } from "@/lib/userUtils";

export { getDisplayName };

/** Profile shape expected by AssigneeSelector. Matches the subset of fields used for display and identification. */
export interface AssigneeProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
}

interface AssigneeSelectorProps {
  /**
   * The assignees, in the ticket's own order — `[0]` is the primary. Callers
   * pass the column straight through; the picker maintains the ordering.
   */
  assigneeIds: string[];
  onAssigneeIdsChange: (ids: string[]) => void;
  profiles: AssigneeProfile[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showSearch?: boolean;
  modal?: boolean;
  placeholder?: string;
  /** Show badges with X remove buttons in the trigger */
  removableBadges?: boolean;
  /** Show selected assignee badges with remove buttons below the trigger */
  showSelectedBadges?: boolean;
  contentClassName?: string;
  triggerClassName?: string;
  /** Custom trigger element (replaces default button trigger) */
  children?: React.ReactNode;
}

/**
 * The people half of the assignment model — a thin adapter over
 * AssignmentPicker, which teams use too. Everything about "assign several,
 * mark one primary" lives there; what stays here is the person-shaped row
 * (avatar + display name) and the three triggers four call sites need.
 */
const AssigneeSelector = ({
  assigneeIds,
  onAssigneeIdsChange,
  profiles,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showSearch = true,
  modal = false,
  placeholder = "Select assignees...",
  removableBadges = false,
  showSelectedBadges = false,
  contentClassName,
  triggerClassName,
  children,
}: AssigneeSelectorProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const onOpenChange = controlledOnOpenChange ?? setInternalOpen;

  const candidates = useMemo<AssignmentCandidate[]>(
    () =>
      profiles.map((profile) => ({
        id: profile.id,
        name: getDisplayName(profile),
        searchValue: `${profile.first_name || ""} ${profile.last_name || ""} ${profile.email}`,
        render: (
          <>
            <UserAvatar
              avatarUrl={profile.avatar_url}
              firstName={profile.first_name}
              lastName={profile.last_name}
              email={profile.email}
              className="h-5 w-5 shrink-0"
              fallbackClassName="text-xs"
            />
            <span className="truncate text-sm">{getDisplayName(profile)}</span>
          </>
        ),
      })),
    [profiles],
  );

  const defaultTrigger = removableBadges ? (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        "w-full justify-start bg-card text-foreground border-border hover:bg-muted/50 hover:text-foreground h-auto min-h-[40px] py-2",
        triggerClassName,
      )}
    >
      <div className="flex flex-wrap gap-1 flex-1">
        {assigneeIds.length > 0 ? (
          assigneeIds.map((id) => {
            const profile = profiles.find((p) => p.id === id);
            return profile ? (
              <Badge key={profile.id} variant="secondary" className="bg-muted text-foreground hover:bg-muted">
                {getDisplayName(profile)}
                <X
                  className="ml-1 h-3 w-3 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssigneeIdsChange(assigneeIds.filter((aid) => aid !== profile.id));
                  }}
                />
              </Badge>
            ) : null;
          })
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </div>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  ) : (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        "w-full justify-between bg-card text-foreground border-border hover:bg-muted/50 hover:text-foreground",
        triggerClassName,
      )}
    >
      {assigneeIds.length > 0 ? `${assigneeIds.length} selected` : placeholder}
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  const selectedBadges =
    showSelectedBadges && assigneeIds.length > 0 ? (
      <div className="flex flex-wrap gap-2 mt-2">
        {assigneeIds.map((id) => {
          const profile = profiles.find((p) => p.id === id);
          if (!profile) return null;
          return (
            <div
              key={id}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-sm"
            >
              <UserAvatar
                avatarUrl={profile.avatar_url}
                firstName={profile.first_name}
                lastName={profile.last_name}
                email={profile.email}
                className="h-5 w-5"
                fallbackClassName="text-[10px]"
              />
              <span className="text-foreground">{getDisplayName(profile)}</span>
              <X
                className="h-3.5 w-3.5 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssigneeIdsChange(assigneeIds.filter((aid) => aid !== id));
                }}
              />
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <>
      <AssignmentPicker
        selectedIds={assigneeIds}
        onSelectedIdsChange={onAssigneeIdsChange}
        candidates={candidates}
        open={open}
        onOpenChange={onOpenChange}
        showSearch={showSearch}
        modal={modal}
        searchPlaceholder="Search assignees..."
        emptyLabel="No assignee found."
        hint="★ is the primary assigned"
        // No `w-full` here: on a popover that resolves against the trigger's
        // containing block, which made this picker a different width in every
        // place it is used. The picker owns its width.
        contentClassName={contentClassName}
      >
        {children || defaultTrigger}
      </AssignmentPicker>
      {selectedBadges}
    </>
  );
};

export default AssigneeSelector;
